import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { StatusBadge } from '../../components/LiveIndicator';
import { 
  CheckCircle,
  Trophy,
  AlertTriangle,
  Loader2,
  Medal
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

function getRequiredPlaces(numRunners) {
  // Fallback: auto-calculate if num_places not set on race
  if (numRunners >= 16) return 4;
  if (numRunners >= 8) return 3;
  if (numRunners >= 5) return 2;
  return 1;
}

function getPlaceLabels(count) {
  const labels = ['1st (Winner)', '2nd Place', '3rd Place', '4th Place'];
  return labels.slice(0, count);
}

function getPlacesDescription(numPlaces, numRunners) {
  if (numPlaces === 1) return `${numRunners} runners — Win only`;
  if (numPlaces === 2) return `${numRunners} runners — 1st & 2nd`;
  if (numPlaces === 3) return `${numRunners} runners — 1st, 2nd & 3rd`;
  return `${numRunners} runners — 1st, 2nd, 3rd & 4th`;
}

export function Settlement() {
  const [races, setRaces] = useState([]);
  const [runners, setRunners] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRace, setSelectedRace] = useState(null);
  const [placedSelections, setPlacedSelections] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [settling, setSettling] = useState(false);
  const [settlementResult, setSettlementResult] = useState(null);

  const fetchRaces = async () => {
    try {
      const response = await axios.get(`${API}/races`);
      setRaces(response.data.filter(r => ['open', 'suspended', 'closed'].includes(r.status)));
    } catch (error) {
      toast.error('Failed to load races');
    } finally {
      setLoading(false);
    }
  };

  const fetchRunners = async (raceId) => {
    try {
      const response = await axios.get(`${API}/runners?race_id=${raceId}`);
      setRunners(prev => ({ ...prev, [raceId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch runners:', error);
    }
  };

  useEffect(() => {
    fetchRaces();
  }, []);

  useEffect(() => {
    if (selectedRace) {
      fetchRunners(selectedRace.id);
    }
  }, [selectedRace]);

  const handleSelectRace = (race) => {
    setSelectedRace(race);
    setPlacedSelections({});
    setSettlementResult(null);
  };

  const numRunners = selectedRace?.num_runners || runners[selectedRace?.id]?.length || 0;
  const requiredPlaces = selectedRace?.num_places || getRequiredPlaces(numRunners);
  const placeLabels = getPlaceLabels(requiredPlaces);

  const handlePlaceSelect = (placeIndex, runnerId) => {
    setPlacedSelections(prev => ({ ...prev, [placeIndex]: runnerId }));
  };

  const getAvailableRunners = (placeIndex) => {
    const raceRunners = runners[selectedRace?.id] || [];
    const selectedIds = Object.entries(placedSelections)
      .filter(([idx]) => parseInt(idx) !== placeIndex)
      .map(([, id]) => id);
    return raceRunners.filter(r => !selectedIds.includes(r.id));
  };

  const allPlacesSelected = Object.keys(placedSelections).length === requiredPlaces &&
    Object.values(placedSelections).every(v => v);

  const handleSettle = async () => {
    if (!selectedRace || !allPlacesSelected) return;

    setSettling(true);
    try {
      const winningRunnerId = placedSelections[0];
      const placedRunnerIds = [];
      for (let i = 1; i < requiredPlaces; i++) {
        placedRunnerIds.push(placedSelections[i]);
      }

      const response = await axios.post(`${API}/settle`, {
        race_id: selectedRace.id,
        winning_runner_id: winningRunnerId,
        placed_runner_ids: placedRunnerIds
      });
      
      setSettlementResult(response.data);
      toast.success('Race settled successfully!');
      setShowConfirm(false);
      fetchRaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to settle race');
    } finally {
      setSettling(false);
    }
  };

  const getRunnerDisplay = (runnerId) => {
    const r = runners[selectedRace?.id]?.find(runner => runner.id === runnerId);
    return r ? `#${r.number} ${r.name}` : '';
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="settlement-page">
        <div>
          <h1 className="font-heading text-3xl font-bold text-zinc-100">Market Settlement</h1>
          <p className="text-sm text-zinc-500 mt-1">Declare winners and placed runners, then settle markets</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Races List */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-zinc-100 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-blue-500" />
                Races to Settle
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-zinc-800 animate-pulse rounded" />
                  ))}
                </div>
              ) : races.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No races available for settlement</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {races.map(race => (
                    <div 
                      key={race.id}
                      onClick={() => handleSelectRace(race)}
                      className={`p-3 rounded border cursor-pointer transition-colors ${
                        selectedRace?.id === race.id 
                          ? 'bg-blue-600/20 border-blue-600/50' 
                          : 'bg-zinc-800/50 border-zinc-800 hover:border-zinc-700'
                      }`}
                      data-testid={`settle-race-${race.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-zinc-100">{race.venue}</p>
                          <p className="text-xs text-zinc-500">
                            {race.time} &middot; {new Date(race.date).toLocaleDateString()} &middot; {race.num_runners} runners
                          </p>
                        </div>
                        <StatusBadge status={race.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Settlement Panel */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-zinc-100 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                {selectedRace ? `Settle: ${selectedRace.venue} ${selectedRace.time}` : 'Select a Race'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedRace ? (
                <div className="text-center py-8 text-zinc-500">
                  <p>Select a race to settle</p>
                </div>
              ) : settlementResult ? (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded">
                    <h3 className="font-medium text-emerald-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Settlement Complete
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p className="text-zinc-300">
                        Winner: <span className="font-bold text-zinc-100">{settlementResult.winning_runner}</span>
                      </p>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-zinc-500">Total Bets</p>
                          <p className="font-mono text-lg text-zinc-100">{settlementResult.total_bets}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Winning Bets</p>
                          <p className="font-mono text-lg text-emerald-400">{settlementResult.winning_bets}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Collected</p>
                          <p className="font-mono text-lg text-blue-400">&euro;{settlementResult.total_collected?.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Payout</p>
                          <p className="font-mono text-lg text-red-400">&euro;{settlementResult.total_payout?.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-zinc-800">
                        <p className="text-zinc-500">Net Profit</p>
                        <p className={`font-mono text-2xl font-bold ${settlementResult.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          &euro;{settlementResult.net_profit?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    className="w-full"
                    variant="outline"
                    onClick={() => {
                      setSelectedRace(null);
                      setSettlementResult(null);
                      setPlacedSelections({});
                    }}
                    data-testid="settle-another-btn"
                  >
                    Settle Another Race
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Places info */}
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                    <p className="text-sm text-blue-300 font-medium" data-testid="places-description">
                      {getPlacesDescription(requiredPlaces, numRunners)}
                    </p>
                  </div>

                  {/* Place selectors */}
                  {placeLabels.map((label, index) => (
                    <div key={index} className="space-y-2">
                      <Label className="text-zinc-300 flex items-center gap-2">
                        {index === 0 ? (
                          <Trophy className="w-4 h-4 text-yellow-400" />
                        ) : (
                          <Medal className="w-4 h-4 text-zinc-400" />
                        )}
                        {label}
                      </Label>
                      <Select 
                        value={placedSelections[index] || ''} 
                        onValueChange={(val) => handlePlaceSelect(index, val)}
                      >
                        <SelectTrigger 
                          className="bg-black border-zinc-800 text-zinc-100" 
                          data-testid={`place-select-${index}`}
                        >
                          <SelectValue placeholder={`Select ${label.toLowerCase()}...`} />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {getAvailableRunners(index).map(runner => (
                            <SelectItem key={runner.id} value={runner.id}>
                              #{runner.number} {runner.name} ({runner.odds_numerator}/{runner.odds_denominator})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}

                  {allPlacesSelected && (
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-yellow-400 font-medium">Confirmation Required</p>
                          <div className="text-xs text-zinc-400 mt-1 space-y-0.5">
                            {placeLabels.map((label, idx) => (
                              <p key={idx}>
                                <span className="text-zinc-500">{label}:</span>{' '}
                                <span className="font-bold text-zinc-100">{getRunnerDisplay(placedSelections[idx])}</span>
                              </p>
                            ))}
                          </div>
                          <p className="text-xs text-zinc-500 mt-2">
                            This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={!allPlacesSelected}
                    onClick={() => setShowConfirm(true)}
                    data-testid="settle-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Settle Race
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-zinc-100 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Confirm Settlement
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-zinc-300">
                Are you sure you want to settle <span className="font-bold text-zinc-100">{selectedRace?.venue} {selectedRace?.time}</span>?
              </p>
              <div className="space-y-1.5">
                {placeLabels.map((label, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    {idx === 0 ? (
                      <Trophy className="w-4 h-4 text-yellow-400" />
                    ) : (
                      <Medal className="w-4 h-4 text-zinc-400" />
                    )}
                    <span className="text-zinc-500">{label}:</span>
                    <span className="font-bold text-emerald-400">{getRunnerDisplay(placedSelections[idx])}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-zinc-500">
                Winning bets will be paid out and losing bets marked as lost.
              </p>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={handleSettle}
                disabled={settling}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-settle-btn"
              >
                {settling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Settling...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm Settlement
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
